/** Older API body (pre–`security_otp_failure_block_minutes` in copy). Detected via [isOtpDeliveryPolicyBlockedMessage]; replaced in UI by [normalizeOtpPolicyBlockedUserMessage]. */
const LEGACY_OTP_API_TRY_AGAIN_LATER_EXACT =
  'Too many delivery attempts for this email or domain. Please try again later.'

/** Matches API `ConfigurationsService` default when `security_otp_failure_block_minutes` is missing. */
export function parseOtpFailureBlockMinutesFromConfig(configValue: unknown): number {
  const n = parseInt(String(configValue ?? '').trim(), 10)
  if (Number.isFinite(n) && n >= 1) {
    return n
  }
  return 15
}

/** Same wording as API `buildOtpFailureSafeCheckBlockedMessage` for client-side fallback copy. */
export function buildOtpDeliveryPolicyBlockedMessage(blockMinutes: number): string {
  const n = Math.max(1, Math.floor(Number(blockMinutes)) || 1)
  const unit = n === 1 ? 'minute' : 'minutes'
  return `Too many delivery attempts for this email or domain. Please wait ${n} ${unit} before trying OTP again.`
}

/** True when the API indicates OTP delivery is blocked (`security_otp_failure_*` / safe-check). */
export function isOtpDeliveryPolicyBlockedMessage(message: unknown): boolean {
  if (typeof message !== 'string') {
    return false
  }
  const trimmed = message.trim()
  return trimmed.startsWith('Too many delivery attempts for this email or domain')
}

/**
 * When the API still returns legacy “try again later”, show minutes from config instead.
 * Otherwise returns the API message unchanged (already includes dynamic minutes).
 */
export function normalizeOtpPolicyBlockedUserMessage(
  message: unknown,
  blockMinutes: number
): string {
  const built = buildOtpDeliveryPolicyBlockedMessage(blockMinutes)
  if (typeof message !== 'string') {
    return built
  }
  const t = message.trim()
  if (!isOtpDeliveryPolicyBlockedMessage(t)) {
    return t
  }
  if (t === LEGACY_OTP_API_TRY_AGAIN_LATER_EXACT) {
    return built
  }
  return t
}

/** Mirrors API `fallbackReason` strings; used for user-facing copy only. */
export type OtpFallbackReason =
  | 'email_dns_unrouteable'
  | 'email_provider_error'
  | 'email_send_timeout'
  | null

export interface OtpDeliveryMetadata {
  requestedChannel: 'email' | 'mobile'
  actualChannel: 'email' | 'sms'
  fallbackReason: OtpFallbackReason
}

export function getOtpFallbackExplanation(reason: OtpFallbackReason): string | null {
  switch (reason) {
    case 'email_dns_unrouteable':
    case 'email_provider_error':
    case 'email_send_timeout':
      return "We couldn't deliver the code via email, so we sent it to your mobile instead."
    default:
      return null
  }
}

export function shouldShowEmailToSmsFallbackNotice(
  meta: OtpDeliveryMetadata | null | undefined
): boolean {
  return Boolean(
    meta && meta.requestedChannel === 'email' && meta.actualChannel === 'sms' && meta.fallbackReason
  )
}
